import React, { useContext, useRef, useState } from "react";
import { withTranslation } from "react-i18next";
import { postFile } from "../../../utils/requests/completions";
import LoadingSpinner from "../../loading/LoadingSpinner";
import { CheckCircleIcon } from "@heroicons/react/24/solid";
import { ArrowUpTrayIcon } from "@heroicons/react/24/outline";
import { VariablesContext } from "../../../auth/AppProvider";
import { FormLoadingContext } from "../../../pages/Completion/Completion";
import ImageCrop from "./ImageCrop";

const acceptedExtensions = ["jpg", "jpeg", "png", "pdf", "pptx", "csv", "docx", "xlsx"];
const imageExtensions = ["jpg", "jpeg", "png"];



function FileUpload({ value, msalInstance, id, formId, candidature, contactId, questionId, templateId, t, onChange, secondaryObjects, ...newProps }) {
  const variables = useContext(VariablesContext);
  const [, setFormLoading] = useContext(FormLoadingContext);
  const [restartSubmission, setrestartSubmission] = useState(false);
  const [loadingUploadDoc, setLoadingUploadDec] = useState("");
  const [error, setError] = useState("");
  const [fileName, setFileName] = useState("");
  const inputRef = useRef(null);

  const [showCropper, setShowCropper] = useState(false);
  const [imageSrc, setImageSrc] = useState(null);
  const [fileToUpload, setFileToUpload] = useState(null);

  const handleCropComplete = (croppedFile) => {
    setShowCropper(false);
    setFileToUpload(null);
    setImageSrc(null);
    uploadDocument(croppedFile);
  };

  const handleCropCancel = (errorMessage) => {
    setShowCropper(false);
    setImageSrc(null);
    setFileToUpload(null);
    setrestartSubmission(true);
    if (errorMessage) setError(errorMessage);
  };

  const openCropper = (file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.addEventListener('load', () => {
      setImageSrc(reader.result);
      setShowCropper(true);
    });
    reader.readAsDataURL(file);
  };

  const isImageFile = (file) => {
    if (!file || !file.name) return false;
    return imageExtensions.includes(file.name.split('.').pop().toLowerCase());
  };

  function uploadDocument(file) {
    setFormLoading(true);
    setLoadingUploadDec("waiting");
    if (file.size > 5000000) {
      setError(t("File size cannot exceed more than 5MB"));
      setLoadingUploadDec("nok");
      setFormLoading(false);
      setrestartSubmission(true);
      setFileToUpload(null);
    } else if (!acceptedExtensions.includes(file.name.split('.').pop().toLowerCase())) {
      setError(t("Vous ne pouvez télécharger que des fichiers aux formats suivants : JPG, JPEG, PNG, PDF, PPTX, CSV, DOCX, XLSX. Veuillez vérifier le type de votre fichier et réessayer."));
      setLoadingUploadDec("nok");
      setFormLoading(false);
      setrestartSubmission(true);
      setFileToUpload(null);
    } else {
      postFile(msalInstance, variables, file, formId, candidature, contactId, questionId, value, templateId, secondaryObjects).then(async res => {
        if (res.ok) {
          setrestartSubmission(false);
          setFormLoading(false);
          setLoadingUploadDec("ok");
          setFileName(file.name);
          var result = await res.json();
          onChange(result.id);
        } else {
          setFormLoading(false);
          setLoadingUploadDec("nok");
          setrestartSubmission(true);
        }
      }).catch(() => {
        setFormLoading(false);
        setLoadingUploadDec("nok");
        setrestartSubmission(true);
      });
    }
  }

  const handleFileSelect = (file) => {
    if (!file) return;
    setError("");
    if (isImageFile(file)) {
      setFileToUpload(file);
      openCropper(file);
    } else {
      uploadDocument(file);
      setFileToUpload(null);
    }
  };

  const isUploaded = value && !restartSubmission;
  const canOpenFilePicker = loadingUploadDoc !== "waiting" && !isUploaded;

  const openFilePicker = () => {
    if (!canOpenFilePicker || !inputRef.current) return;
    inputRef.current.click();
  };

  const reuploadFile = () => {
    setFileToUpload(null);
    setrestartSubmission(true);
    setError("");
  };

  if (showCropper) {
    return (
      <ImageCrop
        imageSrc={imageSrc}
        onCropComplete={handleCropComplete}
        onCancel={handleCropCancel}
        t={t}
        fileToUpload={fileToUpload}
      />
    );
  }

  return (
    <div
      className={`flex items-center gap-4 rounded-xl border border-dashed p-4 transition-colors ${isUploaded ? "border-gray-200 bg-white" : "border-gray-200 bg-gray-50 hover:bg-white"} ${loadingUploadDoc !== "waiting" ? "cursor-pointer" : ""}`}
      onClick={() => canOpenFilePicker ? openFilePicker() : loadingUploadDoc !== "waiting" ? reuploadFile() : null}
      role={canOpenFilePicker ? "button" : undefined}
      tabIndex={canOpenFilePicker ? 0 : -1}
    >
      {loadingUploadDoc === "waiting" ? (
        <LoadingSpinner />
      ) : isUploaded ? (
        <>
          <CheckCircleIcon className="h-5 w-5 flex-shrink-0" style={{ color: "var(--primary-color)" }} />
          <div className="min-w-0">
            <p className="text-sm font-semibold text-gray-800 truncate">{fileName || t("File uploaded successfully")}</p>
            <p className="text-xs text-gray-400">{t("Fichier ajouté — cliquer pour remplacer")}</p>
          </div>
        </>
      ) : (
        <>
          <ArrowUpTrayIcon className="h-5 w-5 flex-shrink-0" style={{ color: "var(--primary-color)" }} />
          <div className="min-w-0">
            <p className="text-sm font-semibold text-gray-700">{t("Cliquer pour téléverser")}</p>
            <p className={`text-xs ${error ? "text-red-500" : "text-gray-400"}`}>
              {error ? error : "PDF, JPG ou PNG · max 5 Mo"}
            </p>
          </div>
        </>
      )}
      <input
        ref={inputRef}
        id={id ? id : "file-upload"}
        name={id ? id : "file-upload"}
        type="file"
        className="hidden sr-only"
        accept={acceptedExtensions.map(ext => "." + ext).join(",")}
        onChange={(event) => handleFileSelect(event.target.files[0])}
        {...newProps}
      />
    </div>
  );
}

export default withTranslation()(FileUpload);

